import React, { useState, useEffect } from 'react';
import { Star, X, AlertCircle, CheckCircle, Store } from 'lucide-react';
import { ratingService } from '../../../services/ratingService';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';
import { imageUploadService } from '../../../utils/imageUploadService';

const ReviewModal = ({ isOpen, onClose, product, component, seller, user, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [charCount, setCharCount] = useState(0);

  const minChars = 50;
  const maxChars = 2000;

  useEffect(() => {
    if (isOpen && user && (product || component || seller)) { 
      loadExistingReview();
    }
  }, [isOpen, product, component, seller, user]);

  const loadExistingReview = async () => {
    try {
      setError(null);
      let existingData = null;
      if (component) {
        existingData = await ratingService?.getUserComponentRating(
          component?.id,
          user?.id
        );
      } else if (product) {
        existingData = await ratingService?.getUserProductRating(
          product?.id,
          user?.id
        );
      } else if (seller) {
        existingData = await ratingService?.getUserSellerRating(
          seller?.businessSlug || seller?.createdBy, // Use slug or user ID
          user?.id
        );
      }
      
      if (existingData) {
        setExistingReview(existingData);
        setRating(existingData?.rating);
        setReview(existingData?.review || '');
        setCharCount((existingData?.review || '')?.length);
      }
    } catch (err) {
      console.error('Error loading existing review:', err);
    }
  };

  const handleReviewChange = (e) => {
    const text = e?.target?.value;
    if (text?.length <= maxChars) {
      setReview(text);
      setCharCount(text?.length);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!rating) {
      setError('Please select a star rating');
      return;
    }

    if (review?.length < minChars) {
      setError(`Review must be at least ${minChars} characters`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      if (existingReview) {
        if (component) {
          await ratingService?.updateComponentRating(
            existingReview?.id,
            rating,
            review
          );
        } else if (product) {
          await ratingService?.updateProductRating(
            existingReview?.id,
            rating,
            review
          );
        } else if (seller) {
          await ratingService?.updateSellerRating(
            existingReview?.id,
            rating,
            review
          );
        }
      } else {
        if (component) {
          await ratingService?.createComponentRating(
            component?.globalComponentId || component?.id,
            rating,
            review
          );
        } else if (product) {
          await ratingService?.createProductRating(
            product?.id,
            rating,
            review
          );
        } else if (seller) {
          await ratingService?.createSellerRating(
            seller?.businessSlug || seller?.createdBy, // Use slug or user ID
            rating,
            review
          );
        }
      }

      setSuccess(true);
      
      setTimeout(() => {
        onReviewSubmitted?.();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setReview('');
    setHoveredStar(0);
    setError(null);
    setSuccess(false);
    setExistingReview(null);
    setCharCount(0);
    onClose?.();
  };

  const getRatingDescription = (stars) => {
    const descriptions = {
      1: 'Poor - Significantly below expectations',
      2: 'Fair - Below expectations',
      3: 'Good - Meets expectations',
      4: 'Very Good - Exceeds expectations',
      5: 'Excellent - Far exceeds expectations'
    };
    return descriptions?.[stars] || 'Select a rating';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {existingReview ? `Update Your ${component ? 'Component' : product ? 'Product' : 'Seller'} Review` : `Write a ${component ? 'Component' : product ? 'Product' : 'Seller'} Review`}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-4">
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-green-800">Review Submitted Successfully!</h3>
                <p className="text-green-700 text-sm">Thank you for your feedback!</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Error</h3>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {component && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-4">
              <img
                src={
                  component?.images?.[0] 
                    ? (component.images[0].startsWith('http') ? component.images[0] : imageUploadService.getPublicUrl(component.images[0]))
                    : component?.images_url?.[0] 
                      ? (component.images_url[0].startsWith('http') ? component.images_url[0] : imageUploadService.getPublicUrl(component.images_url[0]))
                      : component?.product?.thumbnailUrl || '/api/placeholder/80/80'
                }
                alt={component?.customName || component?.name}
                className="w-20 h-20 object-cover rounded-lg border border-gray-200"
              />
              <div>
                <h3 className="font-semibold text-gray-900">{component?.customName || component?.name} (for {component?.product?.name || 'Unknown Product'})</h3>
                <p className="text-sm text-gray-600">{formatPrice(component?.price)}</p>
              </div>
            </div>
          )}
          {!component && product && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-4">
              <img
                src={product?.thumbnailPath || '/api/placeholder/80/80'}
                alt={product?.name}
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div>
                <h3 className="font-semibold text-gray-900">{product?.name}</h3>
                <p className="text-sm text-gray-600">{formatPrice(product?.price)}</p>
              </div>
            </div>
          )}
          {seller && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-4">
              {(() => {
                const bannerSrc = seller?.logoUrl || seller?.logo_url || seller?.storefrontBannerUrl || seller?.storefrontBanner_url || seller?.storefront_banner_url || '';
                const resolvedLogoUrl = bannerSrc
                  ? (bannerSrc.startsWith('http') ? bannerSrc : imageUploadService.getPublicUrl(bannerSrc, 'seller-logos'))
                  : '';
                
                if (resolvedLogoUrl) {
                  return (
                    <img
                      src={resolvedLogoUrl}
                      alt={seller?.businessName || seller?.business_name}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                    />
                  );
                }
                return (
                  <div className="w-20 h-20 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                    <Store className="w-10 h-10" />
                  </div>
                );
              })()}
              <div>
                <h3 className="font-semibold text-gray-900">{seller?.businessName || seller?.business_name}</h3>
                {/* You might want to display other seller info here, e.g., location, type */}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating Selection */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Your Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                      className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                    >
                      <Star
                        className={`w-10 h-10 transition-colors ${
                          star <= (hoveredStar || rating)
                            ? 'fill-yellow-400 text-yellow-400' :'text-gray-300 hover:text-yellow-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <span className="text-lg font-medium text-gray-700">
                    {rating} {rating === 1 ? 'Star' : 'Stars'}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 italic">
                {getRatingDescription(hoveredStar || rating)}
              </p>
            </div>

            {/* Review Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Review Guidelines</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Be specific about what you liked or didn't like</li>
                <li>• Mention product quality, features, and performance</li>
                <li>• Include details about how you're using the product</li>
                <li>• Keep it respectful and constructive</li>
                <li>• Avoid mentioning competitors or pricing</li>
              </ul>
            </div>

            {/* Review Text */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Your Review <span className="text-red-500">*</span>
              </label>
              <textarea
                value={review}
                onChange={handleReviewChange}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={product ? "Share your detailed experience with this product. What did you love? What could be improved?" : "Share your detailed experience with this seller. What did you love? What could be improved?"}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm text-gray-600">
                  {charCount < minChars ? (
                    <span className="text-orange-600">
                      {minChars - charCount} more characters needed
                    </span>
                  ) : (
                    <span className="text-green-600">✓ Meets minimum length</span>
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  {charCount} / {maxChars}
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
              <Button
                type="submit"
                disabled={submitting || !rating || charCount < minChars}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </div>
                ) : existingReview ? (
                  'Update Review'
                ) : (
                  'Submit Review'
                )}
              </Button>
              <Button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;